import {
  ShieldCheck,
  LineChart,
  Globe2,
  Globe,
  Cpu,
  Compass,
} from "lucide-react";
import { useState } from "react";
import { trackEvent } from "@/lib/meta-pixel";

export default function InvestmentFeatures() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setMessage({ type: "error", text: "Please enter a valid email address" });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch("https://liveapi.nuqiwealth.com/mail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: "User Name",
          email: email,
          phone: "0000000000",
          message: "Contact for NUQI Digital Wealth",
          type: "CU",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      trackEvent("Lead", { content_name: "Homepage Email Interest" });
      setMessage({
        type: "success",
        text: "Thank you! We will contact you soon.",
      });
      setEmail("");
    } catch (err) {
      setMessage({
        type: "error",
        text:
          err instanceof Error
            ? err.message
            : "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const features = [
    {
      id: "01",
      title: "Secure and Regulated",
      desc: "NUQI Digital Wealth operates under the robust oversight of SEBI regulations, ensuring smooth and compliant product offerings tailored to your requirements. With retail endorsement, NUQI lowers entry barriers, making quality investment opportunities more accessible.",
      icon: ShieldCheck,
    },
    {
      id: "02",
      title: "Tailored Equity Solutions",
      desc: "Gain access to 1,500+ carefully selected stocks, screened through a robust exclusion list of industries and financial ratios to ensure quality and alignment. Additionally, explore 18+ expertly curated equity portfolios tailored to meet diverse financial goals and strategies.",
      icon: LineChart,
    },
    {
      id: "03",
      title: "Portfolio Rebalancing",
      desc: "NUQI's expert team of advisors, alongside a supervisory panel, ensures portfolios are rebalanced every six months to maintain alignment with your investment goals and market conditions.",
      icon: Compass,
    },
    {
      id: "04",
      title: "Asset Diversification",
      desc: "Equity, ETFs, and Mutual Funds help spread your exposure across various asset classes, enhancing portfolio stability and potential returns.",
      icon: Globe2,
    },
    {
      id: "05",
      title: "Robo Advisory",
      desc: "Utilize NUQI's AI-powered Robo Advisory to analyze industry trends and tailor recommendations to your unique risk profile and investment goals, enabling smarter, data-driven decisions.",
      icon: Cpu,
    },
    {
      id: "06",
      title: "Global Market Access (Tentative)",
      desc: "Gain access to 9+ global markets, supported by in-depth fundamental data, empowering informed investment decisions across international opportunities.",
      icon: Globe,
    },
  ];

  return (
    <div className="w-full bg-black text-zinc-200 px-6 md:px-20 py-32 font-plus-jakarta" id="advisory">
      {/* Title Section */}
      <div className="max-w-7xl mx-auto mb-20 grid md:grid-cols-2 gap-10">
        <div className="space-y-4">
          <p className="text-sm tracking-[0.2em] text-[#57c0af]">
            THE ADVANTAGE
          </p>

          <h2 className="text-4xl md:text-5xl font-semibold leading-tight">
            Why NUQI <br /> Digital Wealth?
          </h2>
        </div>

        <p className="text-lg text-zinc-400 mt-8 leading-relaxed">
          We combine the intelligence of modern fintech with institution-grade
          safety, transparency and global accessibility — giving investors a
          platform that is secure, informed and aligned with long-term growth.
        </p>
      </div>

      {/* GRID SECTION */}
      <div className="max-w-7xl mx-auto border border-white/10 rounded-2xl overflow-hidden">
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {features.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                className="p-10 relative group transition-all duration-300
                           hover:bg-white/[0.02] hover:-translate-y-[2px]"
              >
                {/* Background number */}
                <span className="absolute top-6 right-10 text-4xl font-bold text-white/5">
                  {item.id}
                </span>

                {/* Icon */}
                <div className="mb-6">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center border
                               border-[#57c0af]/40 transition-all duration-300
                               group-hover:border-[#57c0af]/80 group-hover:shadow-[0_0_15px_rgba(13,211,255,0.25)]
                               group-hover:scale-[1.07]"
                  >
                    <Icon
                      className="w-6 h-6 text-[#57c0af] transition-all duration-300
                                     group-hover:text-[#57c0af]/90"
                    />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-lg md:text-xl font-semibold mb-3 transition-colors duration-300 group-hover:text-white">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-zinc-400 leading-relaxed transition-colors duration-300 group-hover:text-zinc-300">
                  {item.desc}
                </p>

                {/* Subtle bottom accent on hover */}
                <div
                  className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#57c0af]/60
                             transition-all duration-500 group-hover:w-full"
                />
              </div>
            );
          })}
        </div>

        {/* Contact Us Section */}
        <div className="p-10 md:p-16 flex flex-col items-start justify-center border-t border-white/10 space-y-8">
          <h3 className="text-2xl md:text-3xl font-light text-white max-w-3xl leading-relaxed text-left">
            Simplifying ethical compliance so businesses can operate{" "}
            <span className="text-[#57c0af] font-normal">
              responsibly and confidently.
            </span>
          </h3>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 w-full max-w-2xl"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="flex-1 px-6 py-4 bg-transparent border border-white/20 rounded-full text-white placeholder:text-gray-500 focus:outline-none focus:border-[#57c0af] transition-colors disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-4 bg-[#57c0af] hover:bg-[#48a894] text-black font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(87,192,175,0.5)] flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Sending..." : "Contact Us"}
                <span>→</span>
              </button>
            </div>
            {message && (
              <p
                className={`text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}
              >
                {message.text}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
